import { engineEndpoints, type EngineApi } from '@/lib/contracts'
import { supabase } from './supabase'

const API_BASE_URL = (import.meta.env.VITE_ENGINE_API_BASE_URL ?? '').replace(/\/$/, '')

export interface ApiRequestOptions {
  path: string
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
}

const buildUrl = (path: string) => `${API_BASE_URL}${path}`

export async function apiRequest<T>({ path, method = 'GET', body }: ApiRequestOptions): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`
  }

  // 90s timeout — headhunt sprint can take ~30-60s on larger result sets
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 90_000)

  let response: Response
  try {
    response = await fetch(buildUrl(path), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })
  } catch (err) {
    clearTimeout(timeoutId)
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timed out — the server is taking too long to respond.')
    }
    throw new Error(`Network error — could not reach the server. Check your connection.`)
  }
  clearTimeout(timeoutId)

  if (!response.ok) {
    // Try to surface the real error message from the response body before falling back
    let detail = ''
    try {
      const body = await response.json() as Record<string, unknown>
      detail = (body?.error ?? body?.message ?? '') as string
    } catch {
      // non-JSON body — ignore
    }
    throw new Error(detail || `Engine API error (${response.status})`)
  }

  return (await response.json()) as T
}

export const engineApiClient: EngineApi = {
  listProjects: () => apiRequest({ path: engineEndpoints.projects }),
  createProject: (payload) =>
    apiRequest({ path: engineEndpoints.projects, method: 'POST', body: payload }),
  fetchProject: (projectId) => apiRequest({ path: engineEndpoints.project(projectId) }),
  uploadDocuments: (projectId, payload) =>
    apiRequest({ path: engineEndpoints.documents(projectId), method: 'POST', body: payload }),
  listDocuments: (projectId) =>
    apiRequest({ path: engineEndpoints.documents(projectId) }),
  uploadDocumentFile: async (projectId, file, kind) => {
    const { data: { session } } = await supabase.auth.getSession()
    const formData = new FormData()
    formData.append('file', file)
    formData.append('kind', kind)
    const url = buildUrl(engineEndpoints.documentsUpload(projectId))
    const headers: Record<string, string> = {}
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`
    }
    const response = await fetch(url, { method: 'POST', body: formData, headers })
    if (!response.ok) throw new Error(`Document upload failed (${response.status})`)
    return response.json()
  },
  getContextReview: (projectId) => apiRequest({ path: engineEndpoints.contextReview(projectId) }),
  getIntakeThread: (projectId) => apiRequest({ path: engineEndpoints.intake(projectId) }),
  postIntakeMessage: (projectId, payload) =>
    apiRequest({ path: engineEndpoints.intakeMessages(projectId), method: 'POST', body: payload }),
  patchIntakeHighlights: (projectId, highlights) =>
    apiRequest({ path: engineEndpoints.intakeHighlights(projectId), method: 'PATCH', body: highlights }),
  getDeepProbeQuestions: (projectId) => apiRequest({ path: engineEndpoints.deepProbe(projectId) }),
  submitDeepProbeAnswers: (projectId, payload) =>
    apiRequest({ path: engineEndpoints.deepProbeAnswers(projectId), method: 'POST', body: payload }),
  generateOutputs: (projectId) =>
    apiRequest({ path: engineEndpoints.outputs(projectId), method: 'POST' }),
  getOutputs: (projectId) => apiRequest({ path: engineEndpoints.outputsDraft(projectId) }),
  getConfirmationSummary: (projectId) => apiRequest({ path: engineEndpoints.confirmation(projectId) }),
  advanceProjectStage: (projectId, stage) =>
    apiRequest({ path: engineEndpoints.projectStage(projectId), method: 'PATCH', body: { stage } }),
  listCandidates: (projectId) =>
    apiRequest({ path: engineEndpoints.candidates(projectId) }),
  uploadCandidateCV: async (projectId, file) => {
    const { data: { session } } = await supabase.auth.getSession()
    const formData = new FormData()
    formData.append('file', file)
    const url = buildUrl(engineEndpoints.candidatesCvUpload(projectId))
    const headers: Record<string, string> = {}
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`
    }
    const response = await fetch(url, { method: 'POST', body: formData, headers })
    if (!response.ok) throw new Error(`CV upload failed (${response.status})`)
    return response.json()
  },
  uploadCandidateCsv: async (projectId, file) => {
    const { data: { session } } = await supabase.auth.getSession()
    const formData = new FormData()
    formData.append('file', file)
    const url = buildUrl(engineEndpoints.candidatesCsvUpload(projectId))
    const headers: Record<string, string> = {}
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`
    }
    const response = await fetch(url, { method: 'POST', body: formData, headers })
    if (!response.ok) throw new Error(`Engine API error (${response.status})`)
    return response.json()
  },
  getCandidateScreening: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.candidateScreening(projectId, candidateId) }),
  getCandidateReview: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.candidate(projectId, candidateId) }),
  getCandidateDeepDive: (candidateId) =>
    apiRequest({ path: engineEndpoints.candidateDeepDive(candidateId) }),
  getDashboardSummary: (projectId) => apiRequest({ path: engineEndpoints.dashboard(projectId) }),
  updateOutput: (projectId, outputType, content) =>
    apiRequest({ path: engineEndpoints.outputUpdate(projectId, outputType), method: 'PATCH', body: { content } }),
  getScorecardResponses: (projectId) =>
    apiRequest({ path: engineEndpoints.scorecardResponses(projectId) }),
  saveScorecardResponses: (projectId, responses) =>
    apiRequest({ path: engineEndpoints.scorecardResponses(projectId), method: 'PATCH', body: { responses } }),
  launchSearch: (projectId) =>
    apiRequest({ path: engineEndpoints.searchLaunch(projectId), method: 'POST' }),
  getSearchStatus: (projectId) =>
    apiRequest({ path: engineEndpoints.searchStatus(projectId) }),
  listOutreach: (projectId) =>
    apiRequest({ path: engineEndpoints.outreachList(projectId) }),
  generateOutreach: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.outreachGenerate(projectId, candidateId), method: 'POST' }),
  sendOutreach: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.outreachSend(projectId, candidateId), method: 'POST' }),
  updateOutreachDraft: (projectId, candidateId, emailSubject, emailBody) =>
    apiRequest({ path: engineEndpoints.outreachDraft(projectId, candidateId), method: 'PATCH', body: { emailSubject, emailBody } }),
  getPreScreen: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.preScreenRecord(projectId, candidateId) }),
  submitPreScreen: (projectId, candidateId, responses) =>
    apiRequest({ path: engineEndpoints.preScreenComplete(projectId, candidateId), method: 'POST', body: { responses } }),
  getCandidateEvents: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.candidateEvents(projectId, candidateId) }),
  sendPreScreenLink: (projectId, candidateId) =>
    apiRequest({ path: engineEndpoints.preScreenSendLink(projectId, candidateId), method: 'POST' }),
  getCosts: () =>
    apiRequest({ path: engineEndpoints.costs }),
  finaliseShortlist: (projectId) =>
    apiRequest({ path: engineEndpoints.shortlistFinalise(projectId), method: 'POST' }),
}

// ─── Task API helpers ────────────────────────────────────────────────────────

export const createTask = (payload: {
  title: string
  status: string
  category?: string
  assignee?: string
  notes?: string
  position?: number
}) => apiRequest<Record<string, unknown>>({ path: engineEndpoints.tasks, method: 'POST', body: payload })

export const updateTaskStatus = (id: string, status: string) =>
  apiRequest<{ ok: boolean }>({ path: engineEndpoints.task(id), method: 'PATCH', body: { status } })

export const deleteTask = (id: string) =>
  apiRequest<{ ok: boolean }>({ path: engineEndpoints.task(id), method: 'DELETE' })

export { engineEndpoints }
